import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, BookCheck, Download } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { Badge, Button, Card, Spinner } from '@/components/ui';
import {
  acknowledgePolicy,
  downloadMyPolicyPdf,
  getAcknowledgementConsent,
  listMyPolicies,
  markPolicyViewed,
} from '@/api/services/hrPolicies.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { SignaturePad } from '../letters/SignaturePad';
import { formatLetterDate } from '../letters/letterFormat';
import { ASSIGNMENT_STATUS } from '../policies/policyFormat';

/**
 * One policy: read it, then acknowledge it when asked. The acknowledgement is
 * bound to the fingerprint of the exact document shown, which the server
 * re-checks; the confirmation wording is versioned and kept as evidence.
 */
export function AcknowledgePolicyPage(): React.ReactElement {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [signature, setSignature] = useState<string | null>(null);
  const [consented, setConsented] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const viewed = useRef(false);

  const { data: policies, isLoading } = useQuery({
    queryKey: ['my-policies'],
    queryFn: listMyPolicies,
  });
  const policy = (Array.isArray(policies) ? policies : []).find(
    (entry) => entry.assignmentId === assignmentId
  );
  const awaiting = policy?.awaitingMe === true;
  const drawn = policy?.mode === 'drawn';

  const { data: pdf, error: pdfError } = useQuery({
    queryKey: ['my-policy-pdf', assignmentId],
    queryFn: () => downloadMyPolicyPdf(assignmentId!),
    enabled: Boolean(policy),
    staleTime: Infinity,
  });
  const pdfUrl = useMemo(() => (pdf ? URL.createObjectURL(pdf) : null), [pdf]);
  useEffect(
    () => () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl]
  );

  const { data: consent } = useQuery({
    queryKey: ['policy-consent'],
    queryFn: getAcknowledgementConsent,
    enabled: awaiting,
    staleTime: Infinity,
  });
  const terms = consent ? (drawn ? consent.drawn : consent.click) : null;

  useEffect(() => {
    if (!awaiting || !assignmentId || viewed.current) return;
    viewed.current = true;
    void markPolicyViewed(assignmentId).catch(() => undefined);
  }, [awaiting, assignmentId]);

  const acknowledge = useMutation({
    mutationFn: () =>
      acknowledgePolicy(assignmentId!, {
        consentVersion: terms!.version,
        artifactSha256: policy!.artifactSha256!,
        ...(drawn && signature && { signatureImage: signature }),
      }),
    onSuccess: () => {
      toast.success('Acknowledged. Thank you.');
      void queryClient.invalidateQueries({ queryKey: ['my-policies'] });
    },
    onError: (error) => setFormError(getErrorMessage(error)),
  });

  const ready =
    Boolean(policy?.artifactSha256 && terms && consented) &&
    (!drawn || Boolean(signature)) &&
    !acknowledge.isPending;

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="flex justify-center py-12" role="status">
        <Spinner />
        <span className="sr-only">Loading policy</span>
      </div>
    );
  } else if (!policy) {
    body = (
      <Card>
        <p className="text-sm text-gray-700 dark:text-gray-200">
          This policy is not available to you.
        </p>
      </Card>
    );
  } else {
    const status = policy.overdue
      ? { label: 'Overdue', variant: 'danger' as const }
      : ASSIGNMENT_STATUS[policy.status];
    body = (
      <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card
          header={
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {policy.title}
              </h1>
              <Badge variant={status.variant}>{status.label}</Badge>
            </div>
          }
          contentClassName="p-0"
        >
          {pdfUrl ? (
            <iframe
              title={`${policy.title}, version ${policy.version}`}
              src={pdfUrl}
              className="h-[70vh] min-h-[420px] w-full border-0"
            />
          ) : pdfError ? (
            <p className="p-4 text-sm text-danger" role="alert">
              {getErrorMessage(pdfError)}
            </p>
          ) : (
            <div className="flex justify-center py-12" role="status">
              <Spinner />
              <span className="sr-only">Loading document</span>
            </div>
          )}
          {pdfUrl && (
            <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
              <a
                href={pdfUrl}
                download={`${policy.title} v${policy.version}.pdf`}
                className="inline-flex items-center text-sm font-medium text-primary hover:underline"
              >
                <Download className="mr-1.5 h-4 w-4" aria-hidden />
                Download PDF
              </a>
            </div>
          )}
        </Card>

        <div className="min-w-0 space-y-4">
          {policy.summaryOfChanges && (
            <Card
              header={
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  What changed in version {policy.version}
                </h2>
              }
            >
              <p className="whitespace-pre-line text-sm text-gray-700 dark:text-gray-200">
                {policy.summaryOfChanges}
              </p>
            </Card>
          )}
          {awaiting ? (
            <Card
              header={
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Acknowledge this policy
                </h2>
              }
            >
              <div className="space-y-4">
                {policy.dueAt && (
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Please acknowledge by {formatLetterDate(policy.dueAt)}.
                  </p>
                )}
                {drawn && <SignaturePad onChange={setSignature} disabled={acknowledge.isPending} />}
                <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    checked={consented}
                    onChange={(event) => setConsented(event.target.checked)}
                    disabled={!terms}
                  />
                  <span>{terms?.text ?? 'Loading acknowledgement terms…'}</span>
                </label>
                {formError && (
                  <p className="text-sm text-danger" role="alert">
                    {formError}
                  </p>
                )}
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    disabled={!ready}
                    loading={acknowledge.isPending}
                    onClick={() => {
                      setFormError(null);
                      acknowledge.mutate();
                    }}
                  >
                    {drawn ? 'Sign and acknowledge' : 'Acknowledge'}
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card>
              <p className="text-sm text-gray-700 dark:text-gray-200">
                {policy.acknowledgedAt
                  ? `You acknowledged this version on ${formatLetterDate(policy.acknowledgedAt)}.`
                  : policy.status === 'carried_forward'
                    ? 'Your acknowledgement of an earlier version covers this one.'
                    : policy.status === 'exempted'
                      ? 'HR has exempted you from acknowledging this version.'
                      : 'This policy is for reading. Nothing to acknowledge.'}
              </p>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Self Service', to: '/dashboard' },
        { label: 'My Policies', to: '/my-policies' },
        { label: 'Policy' },
      ]}
      icon={BookCheck}
      title="Policy"
      className="gap-0"
      hideHeader
    >
      <div className="mx-auto w-full max-w-6xl space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/my-policies')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
          All policies
        </Button>
        {body}
      </div>
    </PageShell>
  );
}
